import { Injectable } from '@angular/core';
import { BehaviorSubject, finalize, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

import { RestapiService } from '@app/services/restapi.service';
import {
  CustomInspectionContent,
  RestCustomInspectionContentItemModel,
  RestCustomInspectionModel,
} from '@main-application/boards/interfaces/custom-inspection';
import { ConfirmationModalData } from '@shared/interfaces/modal-data';
import { RestTemplateModel } from '@template/models/rest-template-model.interface';
import { ModalsService } from '@ui-components/modals/modals.service';

@Injectable()
export class CustomInspectionService {
  private readonly route = 'inspections/';
  _templatesLoading = new BehaviorSubject<boolean>(false);
  templatesLoading$ = this._templatesLoading.asObservable();
  constructor(private service: RestapiService, private modalsService: ModalsService) {}

  getInspectionTemplates(preserveInspectionId?: number) {
    this._templatesLoading.next(true);
    return this.service
      .getData<RestTemplateModel[]>(this.route + 'template/list?filterUnavailableTemplates=false')
      .pipe(
        map(templates => {
          if (!templates?.length) {
            return [];
          }
          const filteredTemplates = templates.filter(
            template => template.isAvailable || template.id === preserveInspectionId
          );
          return filteredTemplates.sort((a, b) => {
            if (a.name === b.name) return 0;
            return a.name > b.name ? 1 : -1;
          });
        }),
        finalize(() => {
          this._templatesLoading.next(false);
        })
      );
  }

  getCustomInspectionContent(inspectionId: number) {
    return this.service.getData<RestCustomInspectionContentItemModel[]>(
      this.route + 'getCustomInspectionContent/' + inspectionId
    );
  }

  updateCustomInspectionContent(inspectionId: number, data: RestCustomInspectionContentItemModel[]) {
    return this.service.post<RestCustomInspectionContentItemModel[]>(
      this.route + 'updateCustomInspection/' + inspectionId,
      data
    );
  }

  addCustomInspectionTemplateWithPredefinedItems(data: RestCustomInspectionModel) {
    return this.service.post<number>(this.route + 'template/addCustomInspection', data).pipe(
      switchMap(id => {
        const inspection = new CustomInspectionContent();
        inspection.addItem('Item 1');
        inspection.addItem('Item 2');
        inspection.addItem('Item 3');
        return this.updateCustomInspectionTemplateContent(id, inspection.items).pipe(
          map(items => [id, items] as const)
        );
      })
    );
  }

  addCustomInspectionTemplateWithItems(data: RestCustomInspectionModel, items: RestCustomInspectionContentItemModel[]) {
    return this.service.post<number>(this.route + 'template/addCustomInspection', data).pipe(
      switchMap(id => {
        return this.updateCustomInspectionTemplateContent(id, items).pipe(map(items => [id, items] as const));
      })
    );
  }

  addCustomInspectionTemplate(data: RestCustomInspectionModel) {
    return this.service.post<number>(this.route + 'template/addCustomInspection', data);
  }

  editCustomInspectionTemplate(inspectionId: number, data: RestCustomInspectionModel) {
    return this.service.post<RestCustomInspectionModel>(
      this.route + 'template/editCustomInspection/' + inspectionId,
      data
    );
  }

  updateCustomInspectionTemplateContent(inspectionId: number, data: RestCustomInspectionContentItemModel[]) {
    return this.service.post<RestCustomInspectionContentItemModel[]>(
      this.route + 'template/updateCustomInspectionContent/' + inspectionId,
      data
    );
  }

  getInspectionTemplate(inspectionId: number) {
    return this.service.getData<RestTemplateModel>(this.route + 'template/' + inspectionId);
  }

  getCustomInspectionTemplateContent(templateId: number) {
    return this.service.getData<RestCustomInspectionContentItemModel[]>(
      this.route + 'template/getCustomInspectionContent/' + templateId
    );
  }

  hideInspectionTemplate(template: RestTemplateModel) {
    const removableTemplate = { ...template, isAvailable: false };
    return this.service.post<RestTemplateModel>(this.route + 'template/edit', removableTemplate);
  }

  openRemovalTemplateModal(inspectionTemplate: RestTemplateModel, data: ConfirmationModalData = {}) {
    return this.modalsService.openConfirmationModal(
      {
        ...{
          title: `Delete ${inspectionTemplate.name} template?`,
          titleCss: 'fade-truncate',
          content: `This will delete it as a template from all boards. Are you feeling this confident? 💪`,
          cancelLabel: 'Yikes, no!',
          confirmLabel: 'Yes, delete it',
          confirmColor: 'warn',
        },
        ...data,
      },
      360
    );
  }
}
